import { Host, h } from "@stencil/core";
import { computePosition, autoUpdate, flip, offset, shift } from "@floating-ui/dom";
let id = 0;
export class CoreSelect {
    constructor() {
        this.selectId = `select-${++id}`;
        this._options = [];
        this.toggleOptions = () => {
            if (this.disabled)
                return;
            this.open = !this.open;
        };
        this.selectOption = (option, index) => {
            if (option.disabled)
                return;
            this.selectedOption = option.value;
            this.selectedOptionIndex = index;
            this.coreSelectOptionChange.emit(option.value);
            this.open = false;
        };
        this.handleKeyDown = (event) => {
            const triggerElement = document.getElementById(`${this.selectId}`);
            const selectList = document.getElementById(`${this.selectId}-listbox`);
            const optionsNotDisabled = selectList.querySelectorAll('li.option:not(.option--disabled)');
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                // toggle dropdown on trigger element click
                if (event.composedPath().includes(this.element)) {
                    this.toggleOptions();
                }
            }
            else if (event.key === 'Escape' || event.key === 'Tab') {
                this.open = false;
                this.currentIndex = -1;
                triggerElement.focus();
            }
            else if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.handleArrowDown(optionsNotDisabled);
            }
            else if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.handleArrowUp(optionsNotDisabled);
            }
        };
        this.handleOptionKeyDown = (event, option, index) => {
            const triggerElement = document.getElementById(`${this.selectId}`);
            const selectList = document.getElementById(`${this.selectId}-listbox`);
            const optionsNotDisabled = selectList.querySelectorAll('li.option:not(.option--disabled)');
            if (this.disabled)
                return;
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.selectOption(option, index);
            }
            else if (event.key === 'Escape' || event.key === 'Tab') {
                this.open = false;
                this.currentIndex = -1;
                triggerElement.focus();
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.handleArrowDown(optionsNotDisabled);
            }
            else if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.handleArrowUp(optionsNotDisabled);
            }
        };
        this.setFocusToSelectedOption = () => {
            if (this.disabled)
                return;
            if (this.selectedOptionRef) {
                this.selectedOptionRef.scrollIntoView({ behavior: 'auto', block: 'nearest' });
                this.selectedOptionRef.focus();
            }
        };
        this.getRequirementString = () => {
            if (this.requirement === 'mandatory') {
                return '·\u00a0obligatoire';
            }
            else if (this.requirement === 'optional') {
                return '·\u00a0facultatif';
            }
        };
        this.renderRequirement = () => {
            return this.requirement !== 'none' ? h("span", { class: "field-requirement typography-body-small-italic" }, this.getRequirementString()) : h("slot", { name: "custom-requirement" });
        };
        this.renderLabel = () => {
            return (h("label", { class: "label typography-body-small-bold", id: `${this.selectId}-label` }, this.label, this.renderRequirement()));
        };
        this.renderDescription = () => {
            return this.description && h("span", { class: "description typography-body-small-regular" }, this.description);
        };
        this.renderHeader = (isRenderingDescription) => {
            return (h("div", { class: "select-header" }, this.renderLabel(), isRenderingDescription && this.renderDescription()));
        };
        this.renderErrorMessage = () => {
            return (h("span", { id: `${this.selectId}-error-message`, class: "error-message typography-body-small-italic" }, this.errorMessage));
        };
        this.label = undefined;
        this.description = undefined;
        this.placeholder = 'Select an option';
        this.requirement = 'mandatory';
        this.options = [];
        this.format = 'inside';
        this.disabled = false;
        this.errorMessage = null;
        this.selectedOption = undefined;
        this.selectedOptionIndex = undefined;
        this.open = false;
        this.placement = 'bottom';
        this.inError = false;
        this.currentIndex = undefined;
        this.trigger = undefined;
        this.popover = undefined;
    }
    parseOptionsProp(newValue) {
        if (newValue)
            this.options = JSON.parse(newValue);
    }
    setErrorState(newValue) {
        if (this.errorMessage) {
            this.inError = true;
        }
        else {
            this.inError = false;
        }
        if (newValue) {
            this.errorMessage = newValue;
        }
    }
    async handleOpenChange(newValue, oldValue) {
        if (newValue === oldValue)
            return;
        this.open = newValue;
        if (this.open) {
            await this.handlePopover();
            // to allow transition on popover opening, it is is displayed with opacity 0 before removing the hidden class
            this.popover.style.display = 'block';
            setTimeout(() => {
                this.popover.classList.remove('option-list--hidden');
            }, 100);
        }
        else {
            // to allow transition on popover closing, the hidden class is added before adding display: none
            this.popover.classList.add('option-list--hidden');
            setTimeout(() => {
                this.popover.style.display = 'none';
                // retrieve select list from the body on close
                this.element.querySelector('.select-wrapper').append(this.popover);
            }, 100);
        }
    }
    disconnectedCallback() {
        this.open = false;
    }
    getDataFromProperty(stringToParse) {
        if (stringToParse === 'undefined')
            return;
        try {
            return JSON.parse(stringToParse);
        }
        catch (e) {
            console.error('Could not parse JSON', e);
            if (stringToParse.split(',').length > 0)
                return stringToParse.split(',');
        }
    }
    async updatePosition(reference, floating) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        await computePosition(reference, floating, {
            // choosing placement depending on the alignment property
            placement: 'bottom',
            middleware: [offset(4), flip(), shift()]
        }).then(({ x, y, placement }) => {
            Object.assign(floating.style, {
                top: `${y}px`,
                left: `${x}px`,
                width: `${reference.offsetWidth}px`
            });
            this.placement = placement;
        });
    }
    async handlePopover() {
        const reference = this.element.querySelector('.select-field');
        const floating = document.getElementById(`${this.selectId}-listbox`);
        if (floating) {
            this.popover = floating;
            document.body.append(this.popover);
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            autoUpdate(reference, floating, async () => {
                await this.updatePosition(reference, floating);
            });
        }
    }
    connectedCallback() {
        this.setErrorState(this.errorMessage);
        if (typeof this.options === 'string') {
            this._options = this.getDataFromProperty(this.options);
        }
        else {
            this._options = this.options;
        }
    }
    componentDidLoad() {
        // on click outside close dropdown
        document.addEventListener('click', event => {
            if (!event.composedPath().includes(this.element) && !event.composedPath().includes(document.getElementById(`${this.selectId}-listbox`))) {
                this.open = false;
            }
        });
    }
    // Helper function to handle arrow down navigation
    handleArrowDown(items) {
        if (this.currentIndex < items.length - 1) {
            this.currentIndex++;
        }
        else {
            this.currentIndex = 0; // Wrap to the beginning.
        }
        // @ts-expect-error
        items[this.currentIndex].focus();
    }
    // Helper function to handle arrow up navigation
    handleArrowUp(items) {
        if (this.currentIndex > 0) {
            this.currentIndex--;
        }
        else {
            this.currentIndex = items.length - 1; // Wrap to the end.
        }
        // @ts-expect-error
        items[this.currentIndex].focus();
    }
    handleClick(event) {
        if (!this.element.contains(event.target)) {
            this.open = false;
        }
    }
    renderOptions(option, index) {
        return (h("li", { class: `option typography-body-medium-regular ${index === this.selectedOptionIndex ? 'option--selected' : ''} ${option.disabled ? 'option--disabled' : ''}`, ref: el => {
                if (index === this.selectedOptionIndex)
                    this.selectedOptionRef = el;
            }, onClick: () => {
                this.selectOption(option, index);
            }, onKeyDown: (event) => {
                this.handleOptionKeyDown(event, option, index);
            }, tabIndex: option.disabled ? -1 : 0, role: "option", "aria-selected": index === this.selectedOptionIndex ? 'true' : 'false', "aria-disabled": this.disabled }, h("span", { class: "option-content" }, option.value), h("i", { class: "option-indicator" }, index === this.selectedOptionIndex && (h("span", { class: "icon-box" }, h("core-icon", { name: "check_mark-outline", fill: "currentcolor", size: "number-size-icon-large" }))), option.disabled && (h("span", { class: "icon-box" }, h("core-icon", { name: "lock-solid", size: "number-size-icon-large" }))))));
    }
    renderSection(options, parentIndex) {
        return (h("li", null, h("span", { class: "section-title typography-body-small-bold" }, options.title), h("ul", null, options.options.map((option, optionIndex) => {
            // Index made of the section index following by the option index
            const nestedOptionIndex = `${parentIndex}${optionIndex}`;
            return this.renderOptions(option, nestedOptionIndex);
        }))));
    }
    renderList() {
        if (this._options) {
            return this._options.map((optionOrSection, index) => {
                if (optionOrSection.title) {
                    return this.renderSection(optionOrSection, index);
                }
                else {
                    const option = optionOrSection;
                    return this.renderOptions(option, index);
                }
            });
        }
    }
    render() {
        return (h(Host, { key: '51f5e539b32f69052814fa8ef437b8505cf1e099' }, h("div", { key: '4a04ba18057dc00040ed023f527a38c0272c196f', class: `core-select select-format--${this.format} select--${this.open ? 'open' : 'closed'} ${this.inError ? 'select-in-error' : ''} ${this.disabled ? 'select-disabled' : ''}` }, this.format !== 'inside' && this.format !== 'hidden' && this.renderHeader(this.description !== undefined), h("div", { key: 'bd41667bbac2f0ce8eee9ddb653d80c125a29713', class: "select-wrapper" }, h("div", { key: 'e72469c9f7d2256016f614a284d5bf812b9bf36b', class: "select-field typography-body-medium-regular", "aria-controls": `${this.selectId}-listbox`, "aria-expanded": this.open ? 'true' : 'false', "aria-haspopup": "listbox", "aria-labelledby": this.format !== 'hidden' && `${this.selectId}-label`, "aria-label": this.format === 'hidden' && this.label, "aria-describedby": this.inError && `${this.selectId}-error-message`, id: this.selectId, role: "combobox", tabindex: "0", onClick: this.toggleOptions, onKeyDown: this.handleKeyDown, "aria-disabled": this.disabled }, h("div", { key: '1020d36e2691baf597954eaf59ac20df252d0e3b', class: "field-content" }, this.format === 'inside' && this.renderHeader(false), this.selectedOption ? this.selectedOption : h("span", { class: "field-placeholder" }, this.placeholder)), h("i", { key: '42335dd9bb2a0d27a77efa20d1e3d26c563f1867', class: "field-indicator", "aria-hidden": "true" }, this.inError && (h("span", { class: "icon-box" }, h("svg", { style: { width: 'var(--number-size-icon-large)', height: 'var(--number-size-icon-large)' }, viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, h("circle", { cx: "12", cy: "12", r: "11", fill: "#E71616" }), h("path", { d: "M13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5V13C11 13.5523 11.4477 14 12 14C12.5523 14 13 13.5523 13 13V5Z", fill: "white" }), h("path", { d: "M12 19.5C12.8284 19.5 13.5 18.8284 13.5 18C13.5 17.1716 12.8284 16.5 12 16.5C11.1716 16.5 10.5 17.1716 10.5 18C10.5 18.8284 11.1716 19.5 12 19.5Z", fill: "white" })))), this.disabled ? (h("span", { class: "icon-box" }, h("core-icon", { name: "lock-solid", size: "number-size-icon-large" }))) : (h("span", { class: "icon-box" }, h("core-icon", { class: "open-indicator", name: "chevron_down-outline", size: "number-size-icon-large", fill: "color-neutral-content-medium" }))))), h("div", { key: 'e5e08e094032e80fe43719b5191d891e1c553a21', tabIndex: -1, role: "listbox", class: `core-select-option-list option-list--hidden option-list--${this.placement}`, id: `${this.selectId}-listbox`, "aria-labelledby": `${this.selectId}-label` }, h("ul", { key: '8ab2735fdc2c7fd3a578a3983d3eae06c24bd90c', tabIndex: -1, class: "typography-body-medium-regular" }, this.renderList())), this.inError && this.renderErrorMessage()))));
    }
    static get is() { return "core-select"; }
    static get originalStyleUrls() {
        return {
            "$": ["core-select.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["core-select.css"]
        };
    }
    static get properties() {
        return {
            "label": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The label that accompany the select. It is required for **accessibility** concerns."
                },
                "attribute": "label",
                "reflect": false
            },
            "description": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Description is available on `outside` and `aside` format and will append below the label."
                },
                "attribute": "description",
                "reflect": false
            },
            "placeholder": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Placeholder inside the select."
                },
                "attribute": "placeholder",
                "reflect": false,
                "defaultValue": "'Select an option'"
            },
            "requirement": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "'mandatory' | 'optional' | 'none'",
                    "resolved": "\"mandatory\" | \"none\" | \"optional\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Mention displayed to indicate if the field is mandatory or optional. If none is selected `custom-requirement` slot is available."
                },
                "attribute": "requirement",
                "reflect": false,
                "defaultValue": "'mandatory'"
            },
            "options": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string | OptionType[] | SectionType[]",
                    "resolved": "OptionType[] | SectionType[] | string",
                    "references": {
                        "OptionType": {
                            "location": "local",
                            "path": "/Users/louismarcos/Documents/dev/core-designsystem/src/components/core-select/core-select.tsx",
                            "id": "src/components/core-select/core-select.tsx::OptionType"
                        },
                        "SectionType": {
                            "location": "local",
                            "path": "/Users/louismarcos/Documents/dev/core-designsystem/src/components/core-select/core-select.tsx",
                            "id": "src/components/core-select/core-select.tsx::SectionType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Array of objects describing the options. It should be in strict JSON format.\n\nThe array could be a simple list of options:\n```\nconst options = [\n {\n   \"value\": string,\n   \"disabled\": boolean // can be omitted if not true\n }\n ...\n]\n```\nOr it could be splitted in multiple sections:\n```\nconst options = [\n {\n   \"title\": string, // title of the section\n   \"options\": [\n     {\n       \"value\": string,\n       \"disabled\": boolean // can be omitted if not true\n     }\n     ...\n   ]\n }\n ...\n]\n```"
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "[]"
            },
            "format": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "'inside' | 'outside' | 'aside' | 'hidden'",
                    "resolved": "\"aside\" | \"hidden\" | \"inside\" | \"outside\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Position the label in different place."
                },
                "attribute": "format",
                "reflect": false,
                "defaultValue": "'inside'"
            },
            "disabled": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating the disable state of the Select field. The `aria-disabled` attribute relies on this property."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "errorMessage": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Display an error message under the field and set the field in error state."
                },
                "attribute": "error-message",
                "reflect": false,
                "defaultValue": "null"
            }
        };
    }
    static get states() {
        return {
            "selectedOption": {},
            "selectedOptionIndex": {},
            "open": {},
            "placement": {},
            "inError": {},
            "currentIndex": {},
            "trigger": {},
            "popover": {}
        };
    }
    static get events() {
        return [{
                "method": "coreSelectOptionChange",
                "name": "coreSelectOptionChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the value has changed."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "options",
                "methodName": "parseOptionsProp"
            }, {
                "propName": "errorMessage",
                "methodName": "setErrorState"
            }, {
                "propName": "open",
                "methodName": "handleOpenChange"
            }];
    }
    static get listeners() {
        return [{
                "name": "document:click",
                "method": "handleClick",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=core-select.js.map
